import React, { useEffect, useState, useLayoutEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import LoadingIndicator from '../../components/ActivityIndicator';
import { MainColors } from '../../constants/Colors';
import FontSizes from '../../constants/FontSizes';
import ChoiceCard from '../../components/ChoiceCard';
import MainButton from '../../components/Buttons/MainButton';
import { HeaderBackButton } from '@react-navigation/stack';
import CustomSnackbar from '../../components/CustomSnackbar';
import useFetch from '../../utils/useFetch';

const VoteScreen = ({ route, navigation }) => {
    const isFocused = useIsFocused();
    const [ballot, setBallot] = useState();
    const [ballotItem, setBallotItem] = useState();
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [currentSelectedChoice, setCurrentSelectedChoice] = useState();
    const [selectedChoices, setSelectedChoices] = useState([]);
    const [snackbarVisible, setSnackbarVisible] = useState(false);
    const { get, post, isLoading } = useFetch();

    const { ballotId } = route.params;
    const currentBallotItemIndex = route.params.ballotItemIndex || 0 // default to first ballot item if not provided
    const isFirstPage = currentBallotItemIndex === 0
    const [isLastPage, setIsLastPage] = useState(false)

    useEffect(() => {
        //reset the page state when you visit it a second time
        const unsubscribe = navigation.addListener("focus", () => {
            setBallot(null)
            setBallotItem(null)
            setSelectedChoices({})
        })
        return unsubscribe;
    }, [navigation, currentBallotItemIndex])

    useLayoutEffect(() => {
        navigation.setOptions({
            //   title: value === '' ? 'No title' : value,
            headerLeft: () => (
                <HeaderBackButton
                    onPress={() => {
                        if (currentBallotItemIndex === 0)
                            navigation.navigate('BallotDetails', {
                                ballotId: ballotId
                            })
                        else
                            navigation.navigate('Vote', {
                                ballotId: ballotId,
                                ballotItemIndex: currentBallotItemIndex - 1
                            })
                    }}
                    label="Back"
                    tintColor="#fff"
                />
            )
        });
    }, [navigation, currentBallotItemIndex]);

    useEffect(() => {
        async function getBallotsData() {
            const response = await get(`/api/ballots/${ballotId}`);
            setBallot(response);
        }
        getBallotsData();
    }, [isFocused]);

    useEffect(() => {
        if (ballot) {
            setBallotItem(ballot.ballotItems[currentBallotItemIndex]);
            setIsLastPage(ballot.ballotItems.length - 1 === currentBallotItemIndex);
        } else {
            setBallotItem(null);
            setIsLastPage(false);
        }
        setIsButtonDisabled(true);
    }, [ballot, currentBallotItemIndex]);

    const onChoicePressedHandler = (choice) => {
        setCurrentSelectedChoice(choice);

        const updatedChoiceDictionary = { ...selectedChoices }
        updatedChoiceDictionary[ballotItem.ballotItemId] = choice.choiceId
        setSelectedChoices(updatedChoiceDictionary)
        setIsButtonDisabled(false);
    }

    const submitVoteHandler = async () => {
        setIsButtonDisabled(true);

        await post(`/api/votes`, {
            votes: Object.values(selectedChoices).map(ballotItemChoiceId => ({ ballotItemChoiceId }))
        });

        setSnackbarVisible(true);
    }

    const onSnackbarDismissedHandler = () => {
        setSnackbarVisible(false);
        navigation.navigate('BallotsDashboard');
    }

    return (
        <View style={styles.screen}>
            <LoadingIndicator isLoading={isLoading}>
                {ballot && ballotItem ? (
                    <>
                        <View style={styles.ballotTitleContainer}>
                            <Text style={styles.ballotTitle}>{ballot.title}</Text>
                            <Text style={styles.headerText}>Question {currentBallotItemIndex + 1} of {ballot.ballotItems.length}</Text>
                        </View>
                        <ScrollView style={styles.scrollViewContainer}>
                            <View style={{ paddingHorizontal: 10, paddingVertical: 20 }}>
                                <Text style={styles.ballotItemTitle}>{ballotItem.ballotItemTitle}</Text>
                                {ballotItem.ballotItemDescription ? (
                                    <Text style={styles.ballotItemDesc}>{ballotItem.ballotItemDescription}</Text>
                                ) : (null)}
                            </View>
                            <Text style={styles.headerText}>Select One</Text>
                            {ballotItem.choices.map(choice => (
                                <View style={styles.choiceContainer}>
                                    <ChoiceCard
                                        key={choice.choiceId}
                                        createMode={false}
                                        isSelected={currentSelectedChoice === choice}
                                        onPress={() => onChoicePressedHandler(choice)}
                                        choiceTitle={choice.choiceTitle}
                                        choiceExplanation={choice.choiceExplanation}
                                    />
                                </View>
                            ))}

                            <View style={styles.mainButtonContainer}>
                                {!isLastPage ?
                                    <MainButton
                                        style={{ backgroundColor: isButtonDisabled ? MainColors.lightGray : MainColors.primary }}
                                        onPress={() => navigation.navigate('Vote', {
                                            ballotId,
                                            ballotItemIndex: currentBallotItemIndex + 1
                                        })}
                                        disabled={isButtonDisabled}
                                        text='Next'
                                        accessibilityLabel="Next"
                                        accessibilityHint="Press this button to go to the next ballot item"
                                    />
                                    :
                                    <MainButton
                                        style={{ backgroundColor: isButtonDisabled ? MainColors.lightGray : MainColors.secondary }}
                                        onPress={submitVoteHandler}
                                        disabled={isButtonDisabled}
                                        text="Submit Vote"
                                        accessibilityLabel="Submit Vote"
                                        accessibilityHint="Press this button to submit your vote on this ballot"
                                    />}
                            </View>
                        </ScrollView>
                    </>
                ) : (null)}
            </LoadingIndicator>
            <CustomSnackbar
                message="Your vote has been submitted!"
                visible={snackbarVisible}
                onDismiss={onSnackbarDismissedHandler}
                actionLabel='GO BACK'
                actionOnPress={() => navigation.navigate('BallotsDashboard')}
            />
        </View>
    );
}

export const screenOptions = () => {
    return {
        headerTitle: 'Vote'
    }
}

const styles = StyleSheet.create({
    screen: {
        flex: 1,
    },
    ballotTitleContainer: {
        paddingTop: 20,
        paddingBottom: 10,
        borderBottomColor: MainColors.faintGray,
        borderBottomWidth: 1.2,
    },
    ballotTitle: {
        textAlign: 'center',
        fontSize: FontSizes.large,
        fontWeight: '600',
        color: MainColors.darkGray,
        letterSpacing: 0.5,
        lineHeight: 24,
    },
    headerText: {
        textAlign: 'center',
        margin: 10,
        fontSize: Platform.OS === 'web' ? FontSizes.medium : FontSizes.small,
        fontWeight: '500',
        color: MainColors.mediumGray,
        letterSpacing: 0.5
    },
    scrollViewContainer: {
        paddingHorizontal: 20
    },
    questionContainer: {
        backgroundColor: 'white',
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        paddingVertical: 20,
        paddingHorizontal: 10,
    },
    ballotItemTitle: {
        textAlign: 'center',
        fontSize: FontSizes.large,
        color: MainColors.darkGray,
        fontWeight: '600',
        marginBottom: 10,
        letterSpacing: 0.5,
        lineHeight: 24,
    },
    ballotItemDesc: {
        alignSelf: 'center',
        fontSize: FontSizes.medium,
        color: MainColors.darkGray,
        lineHeight: 28,
        letterSpacing: 0.5
    },
    choiceContainer: {
        marginBottom: 20,
        alignSelf: 'center',
        width: '100%'
    },
    mainButtonContainer: {
        marginVertical: 30
    }
});

export default VoteScreen;